import { Droplets, Sparkles, Zap, Star, Moon, Sun } from 'lucide-react';
import { LightBulb } from '../components/LightBulb';
import { Particles } from '../components/Particles';
import { TypingText } from '../components/TypingText';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../styles/animations.css';
import lumiworksLogo from '../../logos/lumiworks-yellownew.jpeg';
import lumitechLogo from '../../logos/lumitech-darkbluenew.jpeg';
import lumicleanLogo from '../../logos/lumiclean-bluenew.jpeg';
import lumilearnLogo from '../../logos/lumilearn-pinknew.jpeg';

// Navbar component
type Page = 'landing' | 'tech' | 'clean' | 'learn';

const Navbar = ({ 
  isDarkMode, 
  onToggleTheme, 
  onNavigate 
}: { 
  isDarkMode: boolean; 
  onToggleTheme: () => void; 
  onNavigate: (page: Page) => void; 
}) => {
  return (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${isDarkMode ? 'bg-gray-900/80 backdrop-blur-md' : 'bg-white/80 backdrop-blur-md shadow-sm'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link to="/" className="flex-shrink-0 flex items-center">
              <img
                src={lumiworksLogo}
                alt="LumiWorks logo"
                className="h-8 w-8 object-contain"
              />
              <span className={`ml-2 text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Lumiworks
              </span>
            </Link>
          </div>
          
          <div className="flex items-center space-x-2 sm:space-x-4">
            <button
              onClick={() => onNavigate('tech')}
              className={`px-2 sm:px-4 py-2 rounded-lg text-sm sm:text-base font-bold border-2 transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105 ${isDarkMode ? 'bg-blue-600 border-blue-500 text-white hover:bg-blue-500' : 'bg-blue-100 border-blue-400 text-blue-800 hover:bg-blue-200'}`}
            >
              <span className="hidden sm:inline">Lumi</span>Tech
            </button>
            <button
              onClick={() => onNavigate('clean')}
              className={`px-2 sm:px-4 py-2 rounded-lg text-sm sm:text-base font-bold border-2 transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105 ${isDarkMode ? 'bg-cyan-600 border-cyan-500 text-white hover:bg-cyan-500' : 'bg-cyan-100 border-cyan-400 text-cyan-800 hover:bg-cyan-200'}`}
            >
              <span className="hidden sm:inline">Lumi</span>Clean
            </button>
            <button
              onClick={() => onNavigate('learn')}
              className={`px-2 sm:px-4 py-2 rounded-lg text-sm sm:text-base font-bold border-2 transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105 ${isDarkMode ? 'bg-pink-600 border-pink-500 text-white hover:bg-pink-500' : 'bg-pink-100 border-pink-400 text-pink-800 hover:bg-pink-200'}`}
            >
              <span className="hidden sm:inline">Lumi</span>Learn
            </button>
            <button
              onClick={onToggleTheme}
              className={`p-2 rounded-full ${isDarkMode ? 'text-yellow-400 hover:bg-gray-800' : 'text-gray-700 hover:bg-gray-100'}`}
              aria-label="Toggle dark mode"
            >
              {isDarkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

interface LandingPageProps {
  onNavigate: (page: 'landing' | 'tech' | 'clean' | 'learn') => void;
  onToggleTheme: () => void;
  isDarkMode: boolean;
}

function LandingPage({ onNavigate, onToggleTheme, isDarkMode }: LandingPageProps) {
  const [isHovering, setIsHovering] = useState<string | null>(null);
  const [isBulbOn, setIsBulbOn] = useState(false);
  const [showSpark, setShowSpark] = useState(false);
  
  useEffect(() => {
    document.title = 'LumiWorks | Tech, Cleaning & Tutoring Services in Fort Wayne, IN';
  }, []);
  

  const handleButtonClick = (page: string) => {
    onNavigate(page as any);
  };

  const triggerSpark = () => {
    setShowSpark(true);
    setTimeout(() => setShowSpark(false), 500);
  };

  const handleClick = () => {
    setIsBulbOn(prev => !prev);
    triggerSpark();
    onToggleTheme(); // Toggle theme when light bulb is clicked
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <Navbar 
        isDarkMode={isDarkMode} 
        onToggleTheme={onToggleTheme} 
        onNavigate={onNavigate} 
      />
      <div className="absolute inset-0 overflow-hidden pt-16">
        {/* Background with better contrast - more transparent so particles show through */}
        <div className={`absolute inset-0 transition-colors duration-300 ${isDarkMode ? 'bg-slate-900/70' : 'bg-slate-50/60'}`}></div>
        <div className={`absolute inset-0 bg-gradient-to-br ${isDarkMode ? 'from-slate-900/60 via-slate-800/60 to-amber-900/20' : 'from-slate-50/50 via-slate-100/50 to-amber-100/30'}`}></div>
        
        {/* Particles behind content but visible through backgrounds */}
        <div className="absolute inset-0 z-[-1]">
          <Particles />
        </div>
        
        {/* Floating light bulbs - using viewport units for stability */}
        <div style={{
          position: 'fixed',
          top: '25vh',
          left: '-5vw',
          opacity: 0.3,
          zIndex: 0,
          transform: 'translate3d(0, 0, 0)',
          willChange: 'transform',
          backfaceVisibility: 'hidden',
          perspective: 1000
        }}>
          <LightBulb size={150} initialOn={true} />
        </div>
        <div style={{
          position: 'fixed',
          top: '33vh',
          right: '-5vw',
          opacity: 0.3,
          zIndex: 0,
          transform: 'rotate(15deg) translate3d(0, 0, 0)',
          willChange: 'transform',
          backfaceVisibility: 'hidden',
          perspective: 1000
        }}>
          <LightBulb size={180} initialOn={true} />
        </div>
        <div style={{
          position: 'fixed',
          bottom: '25vh',
          left: '25vw',
          opacity: 0.2,
          zIndex: 0,
          transform: 'rotate(-15deg) translate3d(0, 0, 0)',
          willChange: 'transform',
          backfaceVisibility: 'hidden',
          perspective: 1000
        }}>
          <LightBulb size={120} initialOn={true} />
        </div>
        
        {/* Glow effects */}
        <div className="absolute inset-0 bg-gradient-radial from-transparent via-transparent to-amber-500/5"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-transparent via-transparent to-amber-500/5"></div>
      </div>

      <div className="relative z-10 container mx-auto px-6 py-16">
        <div className="text-center mb-16 animate-fade-in-up pt-16">
          <div className="flex flex-col items-center mb-6">
          <div className="relative mb-8 cursor-pointer" style={{ marginTop: '0.5rem' }} onClick={handleClick}>
            <div style={{ zIndex: 10, transform: 'translate3d(0, 0, 0)' }}>
              <LightBulb 
                size={100} 
                className="drop-shadow-[0_0_20px_rgba(250,204,21,0.7)]"
                style={{
                  position: 'relative',
                  transform: 'translate3d(0, 0, 0)',
                  zIndex: 10
                }}
                isOn={isBulbOn}
                showSpark={showSpark}
              />
              <Sparkles className="absolute -top-2 -right-2 text-yellow-300 w-6 h-6 animate-ping" style={{ zIndex: 15 }} />
            </div>
            <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 bg-yellow-500/20 w-32 h-4 rounded-full blur-md group-hover:w-40 transition-all duration-500" style={{ zIndex: 5 }}></div>
          </div>
          <h1 className="text-7xl font-bold mb-4 tracking-tight relative group">
            <TypingText text="Lumiworks" isDarkMode={isDarkMode} />
            <span className={`absolute -top-4 -right-4 ${isDarkMode ? 'text-yellow-400' : 'text-amber-600'} text-4xl animate-bounce`}>
              <Sparkles className="w-8 h-8" />
            </span>
            <span className={`absolute -bottom-2 left-0 h-1 bg-gradient-to-r ${isDarkMode ? 'from-yellow-400 to-amber-500' : 'from-amber-500 to-amber-600'} w-0 group-hover:w-full transition-all duration-1000`}></span>
          </h1>
        </div>

        <div className="max-w-5xl mx-auto px-4">
          <p className={`text-center text-xl ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-16 leading-relaxed animate-fade-in-up delay-100 whitespace-normal`}>
            Welcome to LumiWorks — your one-stop shop for technology, cleaning, and tutoring services. 
            Whether you need a website built, your space cleaned, or personalized learning support, 
            we're here to help. Explore what we offer below.
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            <div 
              className="animate-fade-in-up delay-200 group relative card-shine"
              onMouseEnter={() => setIsHovering('tech')}
              onMouseLeave={() => setIsHovering(null)}
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl shadow-2xl overflow-hidden transform transition-all duration-500 hover:scale-105 hover:shadow-[0_20px_60px_rgba(59,130,246,0.3)] hover:bg-white/20 border border-white/20 h-full flex flex-col hover-lift">
                <div className="bg-white p-8 text-gray-900 relative overflow-hidden group-hover:shadow-[0_0_30px_rgba(30,58,138,0.4)] transition-all duration-500 flex flex-col items-center text-center">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-100/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative z-10 mb-4 transform transition-transform duration-500 group-hover:scale-110">
                    <img
                      src={lumitechLogo}
                      alt="LumiTech logo"
                      className="w-28 h-28 md:w-32 md:h-32 object-contain"
                    />
                  </div>
                  <p className="text-blue-900 font-semibold mb-6 relative z-10">Technology Solutions</p>
                </div>
                <div className="p-6 bg-gray-800/50 backdrop-blur-sm flex-grow flex flex-col">
                  <div className={`mb-6 leading-relaxed flex-grow ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <p>Website and app creation, adult tech literacy training, tech maintenance, and programming services.</p>
                  </div>
                  <button
                    onClick={() => handleButtonClick('tech')}
                    className="w-full bg-blue-900 hover:bg-blue-800 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-lg flex items-center justify-center group/btn animate-pulse-glow"
                    aria-label="Go to LumiTech page"
                  >
                    <Zap className="w-5 h-5 mr-2 group-hover/btn:animate-bounce" />
                    Explore LumiTech
                  </button>
                </div>
              </div>
            </div>

            <div 
              className="animate-fade-in-up delay-300 group relative card-shine"
              onMouseEnter={() => setIsHovering('clean')}
              onMouseLeave={() => setIsHovering(null)}
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl shadow-2xl overflow-hidden transform transition-all duration-500 hover:scale-105 hover:shadow-[0_20px_60px_rgba(6,182,212,0.3)] hover:bg-white/20 border border-white/20 h-full flex flex-col hover-lift">
                <div className="bg-white p-8 text-gray-900 relative overflow-hidden group-hover:shadow-[0_0_30px_rgba(6,182,212,0.4)] transition-all duration-500 flex flex-col items-center text-center">
                  <div className="absolute inset-0 bg-gradient-to-br from-cyan-50/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative z-10 mb-4 transform transition-transform duration-500 group-hover:scale-110">
                    <img
                      src={lumicleanLogo}
                      alt="LumiClean logo"
                      className="w-28 h-28 md:w-32 md:h-32 object-contain"
                    />
                  </div>
                  <p className="text-cyan-700 font-semibold mb-6 relative z-10">Cleaning services</p>
                </div>
                <div className="p-6 bg-gray-800/50 backdrop-blur-sm flex-grow flex flex-col">
                  <div className={`mb-6 leading-relaxed flex-grow ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <p>Window cleaning, home and business cleaning, organizing and decluttering services.</p>
                  </div>
                  <button
                    onClick={() => handleButtonClick('clean')}
                    className="w-full bg-cyan-600 hover:bg-cyan-500 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-lg flex items-center justify-center group/btn animate-pulse-glow"
                    aria-label="Go to LumiClean page"
                  >
                    <Droplets className="w-5 h-5 mr-2 group-hover/btn:animate-bounce" />
                    Explore LumiClean
                  </button>
                </div>
              </div>
            </div>

            <div 
              className="animate-fade-in-up delay-400 group relative card-shine h-full"
              onMouseEnter={() => setIsHovering('learn')}
              onMouseLeave={() => setIsHovering(null)}
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl shadow-2xl overflow-hidden transform transition-all duration-500 hover:scale-105 hover:shadow-[0_20px_60px_rgba(236,72,153,0.3)] hover:bg-white/20 border border-white/20 h-full flex flex-col hover-lift">
                <div className="bg-white p-8 text-gray-900 relative overflow-hidden group-hover:shadow-[0_0_30px_rgba(236,72,153,0.4)] transition-all duration-500 flex flex-col items-center text-center">
                  <div className="absolute inset-0 bg-gradient-to-br from-pink-50/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative z-10 mb-4 transform transition-transform duration-500 group-hover:scale-110">
                    <img
                      src={lumilearnLogo}
                      alt="LumiLearn logo"
                      className="w-28 h-28 md:w-32 md:h-32 object-contain"
                    />
                  </div>
                  <p className="text-pink-600 font-semibold mb-6 relative z-10">Learning services</p>
                </div>
                <div className="p-6 bg-gray-800/50 backdrop-blur-sm flex-grow flex flex-col">
                  <div className={`mb-6 leading-relaxed flex-grow ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <p>One-on-one and group tutoring for Math and Language Arts (K-12), in person or online. Homeschool aide and Adult ASL.</p>
                  </div>
                  <div className="mt-auto">
                    <button
                      onClick={() => handleButtonClick('learn')}
                      className="w-full bg-pink-600 hover:bg-pink-500 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-lg flex items-center justify-center group/btn animate-pulse-glow"
                      aria-label="Go to LumiLearn page"
                    >
                      <Star className="w-5 h-5 mr-2 group-hover/btn:animate-bounce" />
                      Explore LumiLearn
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <footer className="text-center mt-16 text-gray-500 text-sm animate-fade-in-up delay-500">
          Made by LumiTech
        </footer>
      </div>
    </div>
  </div>
  );
}

export default LandingPage;
